/**
 * MEVIA v5.0 - D12: Employee Dashboard Dynamic
 * 
 * Shows evaluation cards conditioned by plan modules + cycle status.
 * Employee NEVER sees: MEVIA Score, HR Indicators, company gap.
 * Handles: ACTIVO, VENCIDO (locks), SIN_CICLO states.
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Label } from './ui/label';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  User, Building2, Mail, Phone, Calendar, Briefcase, GraduationCap,
  Edit, Clock, CheckCircle, AlertCircle, Target, TrendingUp,
  BarChart3, FileText, Activity, Globe, RefreshCw, ChevronRight,
  Award, LogOut, Lock, KeyRound, Eye, EyeOff, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import NotificationBell from './NotificationBell';
import NOM035EmployeePanel from './NOM035EmployeePanel';
import EmployeeActionPlanView from './EmployeeActionPlanView';
import MiPulsoSection from './MiPulsoSection';
import EmployeeExpediente from './EmployeeExpediente';
import { useView } from '../contexts/ViewContext';
import { setQuestionnaireMode } from '../utils/questionnaireMode';
import axios from 'axios';
import logger from '../utils/logger';


const EmployeeDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, refreshUser, changePassword } = useAuth();
  const { language, changeLanguage } = useLanguage();
  
  // M-03: View context for admin-as-employee switching
  const viewCtx = useView();
  const isAdminViewing = viewCtx?.isEmployeeView && viewCtx?.hasAdminContexts;
  
  const [loading, setLoading] = useState(true);
  const [assessments, setAssessments] = useState([]);
  const [pendingAssessments, setPendingAssessments] = useState([]);
  const [completedAssessments, setCompletedAssessments] = useState([]);
  const [pulseSurveys, setPulseSurveys] = useState([]);
  const [enneagramCompleted, setEnneagramCompleted] = useState(false);
  
  // Bug #24: EmployeeExpediente modal
  const [showExpediente, setShowExpediente] = useState(false);
  
  // D12: Dynamic data
  const [d12Data, setD12Data] = useState(null);
  
  // Password change
  const [showPasswordChangeDialog, setShowPasswordChangeDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (user?.force_password_change) setShowPasswordChangeDialog(true);
  }, [user?.force_password_change]);

  const handlePasswordChange = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error(language === 'es' ? 'La contrasena debe tener al menos 6 caracteres' : 'Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(language === 'es' ? 'Las contrasenas no coinciden' : 'Passwords do not match');
      return;
    }
    setChangingPassword(true);
    try {
      const result = await changePassword(newPassword);
      if (result.success) {
        toast.success(language === 'es' ? 'Contrasena actualizada' : 'Password updated');
        setShowPasswordChangeDialog(false);
        setNewPassword('');
        setConfirmPassword('');
      } else { toast.error(result.error); }
    } catch (e) { toast.error('Error'); }
    finally { setChangingPassword(false); }
  };

  useEffect(() => { if (user) loadAllData(); }, [user]);

  const loadAllData = async () => {
    setLoading(true);
    await Promise.all([
      loadD12Data(),
      loadAssessments(),
      loadPulseSurveys(),
      checkEnneagramStatus()
    ]);
    setLoading(false);
  };

  const loadD12Data = async () => {
    try {
      if (!user?.empresa_id || !user?.id) return;
      const res = await axios.get(`/v5/notifications/employee-dashboard-data/${user.empresa_id}/${user.id}`);
      if (res.data.success) setD12Data(res.data.data);
    } catch (e) { /* non-critical, dashboard still works without D12 */ }
  };

  const loadAssessments = async () => {
    try {
      const res = await axios.get(`/assessments`);
      if (res.data.success) {
        const all = res.data.data || [];
        setAssessments(all);
        setPendingAssessments(all.filter(a => a.status !== 'completado'));
        setCompletedAssessments(all.filter(a => a.status === 'completado'));
      }
    } catch (e) { logger.error('Error loading assessments:', e); }
  };

  const loadPulseSurveys = async () => {
    try {
      if (user?.empresa_id) {
        const res = await axios.get(`/v5/pulso/employee/pending`);
        if (res.data.success) setPulseSurveys(res.data.data || []);
      }
    } catch (e) { setPulseSurveys([]); }
  };

  const checkEnneagramStatus = async () => {
    try {
      const res = await axios.get(`/enneagram/assessments`);
      if (res.data.success) {
        const assessmentsList = res.data.data?.assessments || res.data.data || [];
        const list = Array.isArray(assessmentsList) ? assessmentsList : [];
        if (list.length > 0) {
          const completed = list.some(a => a.status === 'completado' || a.status === 'completed');
          setEnneagramCompleted(completed);
        }
      }
    } catch (e) { setEnneagramCompleted(false); }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // D12 derived state
  const isVencido = d12Data?.is_vencido || false;
  const isSinCiclo = d12Data?.is_sin_ciclo || false;
  const moduleAccess = d12Data?.module_access || {};
  const resultsVis = d12Data?.results_visibility || {};

  if (loading) {
    return <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center"><RefreshCw className="w-8 h-8 animate-spin text-teal-600" /></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b-2 border-teal-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <img src="/images/mevia-logo-horizontal.png" alt="MEVIA" className="h-10" />
              <div className="border-l pl-4 border-gray-200">
                <h1 className="text-xl font-bold text-gray-900">{language === 'es' ? 'Mi Portal' : 'My Portal'}</h1>
                <p className="text-sm text-gray-600">{user?.empresa_nombre || 'MEVIA'}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <NotificationBell />
              {/* M-03: Back to admin view button */}
              {isAdminViewing && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={viewCtx.switchToAdminView}
                  className="border-teal-400 text-teal-700 hover:bg-teal-50"
                  data-testid="back-to-admin-btn"
                >
                  <Shield className="h-4 w-4 mr-1" />
                  {language === 'es' ? 'Vista Admin' : 'Admin View'}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => changeLanguage(language === 'es' ? 'en' : 'es')}>
                <Globe className="h-4 w-4 mr-1" />{language === 'es' ? 'EN' : 'ES'}
              </Button>
              <Button variant="ghost" size="sm" onClick={logout}><LogOut className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        
        {/* D12: VENCIDO Banner */}
        {isVencido && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3" data-testid="cycle-vencido-banner">
            <Lock className="w-6 h-6 text-amber-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-amber-800">
                {language === 'es' ? 'Ciclo de evaluacion vencido' : 'Evaluation cycle expired'}
              </p>
              <p className="text-sm text-amber-700">
                {language === 'es' 
                  ? 'Las evaluaciones estan bloqueadas. Contacta a tu administrador.' 
                  : 'Evaluations are locked. Contact your administrator.'}
              </p>
            </div>
          </div>
        )}

        {/* Welcome Banner */}
        <div className="mb-6 p-6 bg-gradient-to-r from-teal-600 to-teal-700 rounded-xl text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">
                {language === 'es' ? `Hola, ${user?.nombre?.split(' ')[0]}!` : `Hello, ${user?.nombre?.split(' ')[0]}!`}
              </h2>
              <p className="text-teal-100 mt-1">
                {language === 'es' 
                  ? 'Aqui puedes gestionar tu desarrollo profesional'
                  : 'Here you can manage your professional development'}
              </p>
            </div>
            <div className="hidden md:flex items-center gap-4">
              <div className="text-center px-4 py-2 bg-white/10 rounded-lg">
                <span className="text-2xl font-bold">{completedAssessments.length + (enneagramCompleted ? 1 : 0)}</span>
                <p className="text-xs text-teal-100">{language === 'es' ? 'Completadas' : 'Completed'}</p>
              </div>
              <div className="text-center px-4 py-2 bg-white/10 rounded-lg">
                <span className="text-2xl font-bold">{pendingAssessments.length + (enneagramCompleted ? 0 : 1)}</span>
                <p className="text-xs text-teal-100">{language === 'es' ? 'Pendientes' : 'Pending'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 1: Profile - Read-only summary with Expediente modal */}
        <Card className="mb-6 overflow-hidden" data-testid="profile-section">
          <CardHeader className="bg-gradient-to-r from-slate-700 to-slate-800 text-white py-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="w-5 h-5" />{language === 'es' ? 'Mi Expediente' : 'My Profile'}
              </CardTitle>
              <Button 
                variant="secondary" 
                size="sm" 
                onClick={() => setShowExpediente(true)} 
                className="bg-white/20 hover:bg-white/30 text-white border-0"
                data-testid="open-expediente-btn"
              >
                <Edit className="w-4 h-4 mr-1" />{language === 'es' ? 'Editar' : 'Edit'}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center"><User className="w-6 h-6 text-teal-600" /></div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{user?.nombre}</p>
                    <p className="text-sm text-gray-500">{user?.rol === 'employee' ? (language === 'es' ? 'Empleado' : 'Employee') : user?.rol}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-gray-400" /><span className="text-gray-600">{user?.email}</span></div>
                  <div className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 text-gray-400" /><span className="text-gray-600">{user?.telefono || '-'}</span></div>
                  <div className="flex items-center gap-2 text-sm"><Building2 className="w-4 h-4 text-gray-400" /><span className="text-gray-600">{user?.empresa_nombre}</span></div>
                </div>
              </div>
              <div className="space-y-3">
                <h4 className="font-medium text-gray-700 flex items-center gap-2"><Briefcase className="w-4 h-4" />{language === 'es' ? 'Datos Laborales' : 'Work Info'}</h4>
                {[
                  { label: language === 'es' ? 'Departamento' : 'Department', val: user?.departamento },
                  { label: language === 'es' ? 'Anos en empresa' : 'Years in company', val: user?.anos_empresa, suffix: '' },
                  { label: language === 'es' ? 'Experiencia total' : 'Total experience', val: user?.anos_experiencia_total, suffix: language === 'es' ? ' anos' : ' years' }
                ].map(({ label, val, suffix }) => (
                  <div key={label} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                    <span className="text-sm text-gray-500">{label}</span>
                    <span className="text-sm font-medium">{val || 0}{suffix || ''}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-3">
                <h4 className="font-medium text-gray-700 flex items-center gap-2"><GraduationCap className="w-4 h-4" />{language === 'es' ? 'Informacion Adicional' : 'Additional Info'}</h4>
                <div className="flex justify-between items-center p-2 bg-slate-50 rounded">
                  <span className="text-sm text-gray-500">{language === 'es' ? 'Educacion' : 'Education'}</span>
                  <span className="text-sm font-medium">{user?.nivel_educacion || '-'}</span>
                </div>
                <div className="flex justify-between items-center p-2 bg-slate-50 rounded">
                  <span className="text-sm text-gray-500">{language === 'es' ? 'Ubicacion' : 'Location'}</span>
                  <span className="text-sm font-medium">{user?.ubicacion || '-'}</span>
                </div>
                <div className="flex justify-between items-center p-2 bg-slate-50 rounded">
                  <span className="text-sm text-gray-500">{language === 'es' ? 'Registro' : 'Joined'}</span>
                  <span className="text-sm font-medium">{formatDate(user?.fecha_registro)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* D12: Section 2 - Pending Evaluations (module-conditional) */}
        <Card className="mb-6" data-testid="pending-evaluations-section">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg text-orange-700">
              <AlertCircle className="w-5 h-5" />
              {language === 'es' ? 'Pendientes por Completar' : 'Pending to Complete'}
              {d12Data?.pending_cards?.length > 0 && <Badge className="bg-orange-500 ml-2">{d12Data.pending_cards.length}</Badge>}
            </CardTitle>
            <CardDescription>
              {isVencido 
                ? (language === 'es' ? 'Las evaluaciones estan bloqueadas hasta que se renueve el ciclo' : 'Evaluations are locked until the cycle is renewed')
                : isSinCiclo
                ? (language === 'es' ? 'Esperando inicio de ciclo de evaluacion' : 'Waiting for evaluation cycle to start')
                : (language === 'es' ? 'Evaluaciones y encuestas que requieren tu atencion' : 'Assessments and surveys that need your attention')
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {d12Data?.all_evaluations_complete ? (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                <p className="font-medium">{language === 'es' ? 'Todo al dia!' : 'All caught up!'}</p>
                <p className="text-sm">{language === 'es' ? 'No tienes evaluaciones pendientes' : 'No pending assessments'}</p>
              </div>
            ) : isSinCiclo ? (
              <div className="text-center py-8 text-gray-500">
                <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">{language === 'es' ? 'Sin ciclo activo' : 'No active cycle'}</p>
                <p className="text-sm">{language === 'es' ? 'Tu administrador aun no ha iniciado un ciclo' : 'Your administrator has not started a cycle yet'}</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {/* D12: Culture assessment card - only if module active */}
                {(moduleAccess.culture !== false) && pendingAssessments.map(assessment => (
                  <div key={assessment.id}
                    className={`flex items-center justify-between p-4 border rounded-lg transition-all ${
                      isVencido ? 'bg-gray-50 opacity-60 cursor-not-allowed' : 'hover:border-blue-400 hover:shadow-sm cursor-pointer bg-white'
                    }`}
                    onClick={() => !isVencido && navigate(`/assessment/${assessment.id}`)}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isVencido ? 'bg-gray-200' : 'bg-blue-100'}`}>
                        {isVencido ? <Lock className="w-5 h-5 text-gray-400" /> : <FileText className="w-5 h-5 text-blue-600" />}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{language === 'es' ? 'Evaluacion Cultural' : 'Culture Assessment'}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Clock className="w-3 h-3" />{language === 'es' ? 'Progreso:' : 'Progress:'} {assessment.progreso_porcentaje || 0}%
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Progress value={assessment.progreso_porcentaje || 0} className="w-20 h-2" />
                      {isVencido ? <Lock className="w-5 h-5 text-amber-500" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
                    </div>
                  </div>
                ))}

                {/* D12: Personality assessment - only if module active */}
                {(moduleAccess.culture !== false) && !enneagramCompleted && (
                  <div className={`flex items-center justify-between p-4 border rounded-lg border-l-4 border-l-teal-500 transition-all ${
                    isVencido ? 'bg-gray-50 opacity-60 cursor-not-allowed' : 'hover:border-teal-400 hover:shadow-sm cursor-pointer bg-white'
                  }`} onClick={() => !isVencido && navigate('/enneagram')}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isVencido ? 'bg-gray-200' : 'bg-teal-100'}`}>
                        {isVencido ? <Lock className="w-5 h-5 text-gray-400" /> : <BarChart3 className="w-5 h-5 text-teal-600" />}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{language === 'es' ? 'Evaluacion de Personalidad' : 'Personality Assessment'}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Clock className="w-3 h-3" />{language === 'es' ? 'Eneagrama - 15-20 min' : 'Enneagram - 15-20 min'}
                        </div>
                      </div>
                    </div>
                    {isVencido ? <Lock className="w-5 h-5 text-amber-500" /> : <Badge className="bg-teal-100 text-teal-700">{language === 'es' ? 'Iniciar' : 'Start'}</Badge>}
                  </div>
                )}

                {/* D12: Pulse survey - only if module active */}
                {moduleAccess.pulse && pulseSurveys.map(pulse => (
                  <div key={pulse.id}
                    className={`flex items-center justify-between p-4 border rounded-lg transition-all ${
                      isVencido ? 'bg-gray-50 opacity-60 cursor-not-allowed' : 'hover:border-purple-400 hover:shadow-sm cursor-pointer bg-white'
                    }`}
                    onClick={() => !isVencido && navigate(`/pulse-survey/${pulse.id}`)}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isVencido ? 'bg-gray-200' : 'bg-purple-100'}`}>
                        {isVencido ? <Lock className="w-5 h-5 text-gray-400" /> : <Activity className="w-5 h-5 text-purple-600" />}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{pulse.titulo}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Clock className="w-3 h-3" />{language === 'es' ? 'Vence:' : 'Due:'} {formatDate(pulse.fecha_fin)}
                        </div>
                      </div>
                    </div>
                    <Badge className="bg-purple-100 text-purple-700">{language === 'es' ? 'Pulso' : 'Pulse'}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* D12: NOM-035 Section - only if module active */}
        {moduleAccess.nom035 && <NOM035EmployeePanel user={user} />}

        {/* M13: Mi Pulso */}
        {moduleAccess.pulse && <MiPulsoSection />}

        {/* D12: Action Plans - only if module active */}
        {moduleAccess.action_plans && (
          <Card className="mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg text-teal-700">
                <Target className="w-5 h-5" />{language === 'es' ? 'Mis Planes de Desarrollo' : 'My Development Plans'}
              </CardTitle>
            </CardHeader>
            <CardContent><EmployeeActionPlanView /></CardContent>
          </Card>
        )}

        {/* D12: My Results - module-conditional */}
        <Card className="mb-6" data-testid="my-results-section">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg text-green-700">
              <CheckCircle className="w-5 h-5" />
              {language === 'es' ? 'Mis Resultados' : 'My Results'}
              {(completedAssessments.length + (enneagramCompleted ? 1 : 0)) > 0 && (
                <Badge variant="outline" className="ml-2 border-green-300 text-green-700">
                  {completedAssessments.length + (enneagramCompleted ? 1 : 0)}
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              {language === 'es' ? 'Historial de tus evaluaciones finalizadas' : 'History of your completed assessments'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {completedAssessments.length === 0 && !enneagramCompleted ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>{language === 'es' ? 'Aun no has completado evaluaciones' : 'No completed assessments yet'}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {completedAssessments.map(assessment => (
                  <div key={assessment.id} className="flex items-center justify-between p-4 border rounded-lg bg-green-50/50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <Award className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{language === 'es' ? 'Evaluacion Cultural' : 'Culture Assessment'}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(assessment.fecha_completado || assessment.updated_at)}</span>
                        </div>
                      </div>
                    </div>
                    <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />{language === 'es' ? 'Completada' : 'Completed'}</Badge>
                  </div>
                ))}
                
                {enneagramCompleted && (
                  <div className="flex items-center justify-between p-4 border rounded-lg bg-teal-50/50 cursor-pointer hover:shadow-sm transition-all"
                    onClick={() => navigate('/enneagram')}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                        <BarChart3 className="w-5 h-5 text-teal-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{language === 'es' ? 'Evaluacion de Personalidad' : 'Personality Assessment'}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500"><span>Eneagrama</span></div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-teal-100 text-teal-700">
                        <CheckCircle className="w-3 h-3 mr-1" />{language === 'es' ? 'Ver resultados' : 'View results'}
                      </Badge>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* D12: My Progress (multi-cycle) - only if applicable */}
        {resultsVis.my_progress && (
          <Card className="mb-6" data-testid="my-progress-section">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg text-blue-700">
                <TrendingUp className="w-5 h-5" />
                {language === 'es' ? 'Mi Progreso' : 'My Progress'}
              </CardTitle>
              <CardDescription>
                {language === 'es' ? 'Comparacion entre ciclos de evaluacion' : 'Comparison between evaluation cycles'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-6 text-gray-500">
                <TrendingUp className="w-10 h-10 mx-auto mb-2 text-blue-300" />
                <p className="text-sm">{language === 'es' ? 'Tu progreso entre ciclos estara disponible aqui' : 'Your cross-cycle progress will be available here'}</p>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Bug #24: EmployeeExpediente Modal */}
      <EmployeeExpediente
        employeeId={user?.id}
        isOpen={showExpediente}
        onClose={() => setShowExpediente(false)}
        onUpdate={() => { refreshUser?.(); }}
        mode="self"
      />

      {/* Password Change Dialog */}
      <Dialog open={showPasswordChangeDialog} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" hideCloseButton={true} onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-teal-600" />{language === 'es' ? 'Cambiar Contrasena' : 'Change Password'}</DialogTitle>
            <DialogDescription>{language === 'es' ? 'Por seguridad, debes establecer una nueva contrasena.' : 'For security, you must set a new password.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Nueva contrasena' : 'New password'}</Label>
              <div className="relative">
                <Input type={showNewPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={language === 'es' ? 'Minimo 6 caracteres' : 'Minimum 6 characters'} className="pr-10" data-testid="new-password-input" />
                <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Confirmar contrasena' : 'Confirm password'}</Label>
              <div className="relative">
                <Input type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={language === 'es' ? 'Repite la contrasena' : 'Repeat password'} className="pr-10" data-testid="confirm-password-input" />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handlePasswordChange} disabled={changingPassword || !newPassword || !confirmPassword} data-testid="change-password-btn">
              {changingPassword ? (language === 'es' ? 'Guardando...' : 'Saving...') : (language === 'es' ? 'Cambiar contrasena' : 'Change password')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeDashboard;
